import React, { useEffect, useState } from "react";
import styled from "styled-components";
import Pagination from "../components/Elements/Pagination";
import { fetchEntityData } from "../utils/RequestHandler";
import LoadingSpinner from "../components/Elements/LoadingSpinner";
import EmptyState from "../components/Elements/EmptyState";

export default function Applications() {
  const [applications, setApplications] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    coverLetter: "",
    resume: null,
  });

  const recordsPerPage = 9;

  useEffect(() => {
    const fetchApplications = async () => {
      try {
        const data = await fetchEntityData("applications");
        if (data.success) {
          setApplications(data.result);
          setFilteredApplications(data.result);
          if (data.result.length > 0) {
            setSelectedApplication(data.result[data.result.length - 1]);
          }
        }
      } catch (error) {
        console.error("Failed to fetch applications:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchApplications();
  }, []);

  useEffect(() => {
    const filtered = applications.filter((application) =>
      `${application.opportunity}`
        .toLowerCase()
        .includes(searchTerm.toLowerCase())
    );
    setFilteredApplications(filtered);
    setCurrentPage(1);
  }, [searchTerm, applications]);

  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredApplications.slice(
    indexOfFirstRecord,
    indexOfLastRecord
  );

  const handlePageChange = (page) => setCurrentPage(page);
  const handleSearchChange = (e) => setSearchTerm(e.target.value);

  const handleApplyClick = () => setIsModalOpen(true);
  const handleModalClose = () => setIsModalOpen(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleFileChange = (e) => {
    setFormData({ ...formData, resume: e.target.files[0] });
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    setIsModalOpen(false);
  };

  return (
    <>
      <Wrapper className="paleBlueGreyBg">
        <Container>
          {/* Sidebar Section */}
          <Sidebar
            style={{ display: "flex", flexDirection: "column", height: "93vh" }}
          >
            <HeaderInfo>
              <SearchBox>
                <input
                  type="text"
                  placeholder="Search application..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
              </SearchBox>
            </HeaderInfo>
            {loading ? (
              <LoadingSpinner message="Loading application" />
            ) : filteredApplications.length > 0 ? (
              <ApplicationList>
                {currentRecords.map((record, index) => (
                  <ApplicationCard
                    key={index}
                    onClick={() => setSelectedApplication(record)}
                  >
                    <h4>{`${record.first_name} ${record.last_name}`}</h4>
                    <p>{`${record.specializations.slice(0, 50)}${
                      record.specializations.length > 50 ? "..." : ""
                    }`}</p>
                  </ApplicationCard>
                ))}
              </ApplicationList>
            ) : (
              <EmptyState message={"No record available."} />
            )}
            {filteredApplications.length > recordsPerPage && (
              <PaginationWrapper>
                <Pagination
                  totalItems={filteredApplications.length}
                  itemsPerPage={recordsPerPage}
                  currentPage={currentPage}
                  onPageChange={handlePageChange}
                />
              </PaginationWrapper>
            )}
          </Sidebar>

          {/* Body Section */}
          <Body>
            {selectedApplication ? (
              <div>
                <HeaderInfo>
                  <h1 className="font40 darkGreenColor extraBold">
                    {selectedApplication.opportunity}
                  </h1>
                </HeaderInfo>

                <Button onClick={handleApplyClick}>Withdraw</Button>
              </div>
            ) : (
              <EmptyState message={"No record available."} />
            )}
          </Body>
        </Container>
      </Wrapper>
      {isModalOpen && (
        <Modal onClick={handleModalClose}>
          <ModalContent>
            <h2>Apply for {selectedApplication?.opportunity}</h2>
            <form onSubmit={handleFormSubmit}>
              <FormField>
                <label>Name</label>
                <input
                  type="text"
                  name="name"
                  placeholder="Enter your name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </FormField>
              <FormField>
                <label>Email</label>
                <input
                  type="email"
                  name="email"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </FormField>
              <FormField>
                <label>Cover Letter</label>
                <textarea
                  name="coverLetter"
                  placeholder="Write a brief cover letter"
                  rows="5"
                  value={formData.coverLetter}
                  onChange={handleInputChange}
                  required
                ></textarea>
              </FormField>
              <FormField>
                <label>Resume</label>
                <input
                  type="file"
                  name="resume"
                  onChange={handleFileChange}
                  required
                />
              </FormField>
              <Button type="submit">Submit Application</Button>
            </form>
            <CloseButton onClick={handleModalClose}>X</CloseButton>
          </ModalContent>
        </Modal>
      )}
    </>
  );
}

// Styled Components
const Wrapper = styled.div`
  padding: 20px 0;
  background-color: #008394;
  background-image: radial-gradient(
      circle at top left,
      rgba(255, 255, 255, 0.2),
      rgba(255, 255, 255, 0) 70%
    ),
    radial-gradient(
      circle at bottom right,
      rgba(255, 255, 255, 0.2),
      rgba(255, 255, 255, 0) 70%
    ),
    url("data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 200 200%22%3E%3Ccircle cx=%22100%22 cy=%22100%22 r=%2290%22 fill=%22%23FFFFFF%22 opacity=%220.1%22 /%3E%3Cpath d=%22M0,100 C50,0 150,0 200,100 C150,200 50,200 0,100 Z%22 fill=%22%23FFFFFF%22 opacity=%220.05%22 /%3E%3C/svg%3E");
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
`;

const Container = styled.div`
  display: flex;
  flex-direction: row;
  gap: 20px;
  padding-top: 80px;
  max-width: 1200px;
  margin: 0 auto;
`;

const Sidebar = styled.div`
  width: 30%;
  background: #fff;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const Body = styled.div`
  width: 70%;
  background: #fff;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const HeaderInfo = styled.div`
  margin-bottom: 20px;
`;

const SearchBox = styled.div`
  margin-top: 10px;
  input {
    width: 93%;
    padding: 10px;
    font-size: 16px;
    border: 1px solid #ddd;
    border-radius: 4px;
  }
`;

const ApplicationList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-top: 20px;
`;

const ApplicationCard = styled.div`
  background: #f7f7f7;
  padding: 15px;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: all 0.2s ease-in-out;

  &:hover {
    background: #e9f5ec;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
  }

  h4 {
    font-size: 18px;
    font-weight: bold;
    margin-bottom: 10px;
  }

  p {
    font-size: 14px;
    color: #666;
  }

  span {
    font-size: 12px;
    color: #999;
  }
`;

// const Section = styled.div`
//   margin-top: 30px;

//   h3 {
//     margin-bottom: 10px;
//   }

//   p {
//     line-height: 1.6;
//   }
// `;

const Button = styled.button`
  background-color: #8A2BE2;
  color: white;
  border: none;
  padding: 10px 20px;
  cursor: pointer;
  &:hover {
    background-color: #45a049;
  }
`;

const PaginationWrapper = styled.div`
  margin-top: 20px;
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalContent = styled.div`
  background: white;
  padding: 20px;
  width: 500px;
  border-radius: 5px;
`;

const FormField = styled.div`
  margin-bottom: 20px;
  label {
    display: block;
    margin-bottom: 10px;
    font-weight: bold;
  }
  input,
  textarea {
    width: 93%;
    padding: 10px;
    border: 1px solid #ccc;
    border-radius: 5px;
  }
  button {
    padding: 10px 20px;
    background-color: #8A2BE2;
    color: #fff;
    border: none;
    border-radius: 5px;
    cursor: pointer;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  font-size: 20px;
  border: none;
  cursor: pointer;
  color: #333;
`;
